import React, { useEffect, useState } from 'react';
import { getActuatorColor } from '../../utils/actionUtils';
import '../../styles/sensorStatusAnimation.css';

/**
 * SensorStatusAnimation - Visual indicator for sensor-to-actuator communication
 * 
 * @param {Object} props
 * @param {string} props.status - Current status: 'idle', 'connecting', 'sending', 'success', 'error'
 * @param {string} props.sensorName - Name of the sensor device
 * @param {string} props.actuatorName - Name of the actuator device
 * @param {string} props.actuatorType - Type of actuator (e.g., 'irrigation', 'fan', 'light')
 */
export default function SensorStatusAnimation({ 
    status = 'idle', 
    sensorName = 'Sensor', 
    actuatorName = 'Actuator',
    actuatorType = 'actuator'
}) {
    const [progress, setProgress] = useState(0);
    const [currentStage, setCurrentStage] = useState('');
    
    useEffect(() => {
        // Reset progress when status changes
        setProgress(0);
        
        if (status === 'connecting') {
            setCurrentStage('Connecting to device...');
            animateProgress(0, 30, 800);
        } else if (status === 'sending') {
            setCurrentStage('Sending command...');
            animateProgress(30, 60, 1000);
        } else if (status === 'verifying') {
            setCurrentStage('Verifying update...');
            animateProgress(60, 90, 1200);
        } else if (status === 'success') {
            setCurrentStage('Success!');
            setProgress(100);
        } else if (status === 'error') {
            setCurrentStage('Failed');
            setProgress(0);
        } else {
            setCurrentStage('');
            setProgress(0);
        }
    }, [status]);
    
    const animateProgress = (start, end, duration) => {
        const startTime = Date.now();
        const animate = () => {
            const elapsed = Date.now() - startTime;
            const percentage = Math.min(elapsed / duration, 1);
            // Use easing function for smoother animation
            const eased = percentage < 0.5 
                ? 2 * percentage * percentage 
                : 1 - Math.pow(-2 * percentage + 2, 2) / 2;
            const currentProgress = start + (end - start) * eased;
            setProgress(currentProgress);
            
            if (percentage < 1) {
                requestAnimationFrame(animate);
            } else {
                setProgress(end);
            }
        };
        requestAnimationFrame(animate);
    };
    
    const getStatusIcon = () => {
        switch (status) {
            case 'success':
                return <i className="ri-checkbox-circle-fill status-icon success-icon"></i>;
            case 'error':
                return <i className="ri-close-circle-fill status-icon error-icon"></i>;
            case 'connecting':
            case 'sending':
            case 'verifying':
                return <i className="ri-loader-4-line status-icon loading-icon"></i>;
            default:
                return <i className="ri-radio-button-line status-icon idle-icon"></i>;
        }
    };
    
    const getActuatorIcon = () => {
        switch (actuatorType?.toLowerCase()) {
            case 'irrigation':
                return 'ri-water-flash-line';
            case 'fan':
                return 'ri-windy-line';
            case 'light':
                return 'ri-lightbulb-line';
            case 'heat':
            case 'heater':
                return 'ri-fire-line';
            default:
                return 'ri-settings-3-line';
        }
    };
    
    const isActive = status !== 'idle';
    const isProcessing = ['connecting', 'sending', 'verifying'].includes(status);
    
    // Get actuator color for idle state
    const actuatorColor = getActuatorColor(actuatorType);
    const actuatorNodeStyle = status === 'idle' ? {
        '--actuator-color': actuatorColor,
        borderColor: `${actuatorColor}66`, // 40% opacity
    } : {};
    
    // Icon color style for idle state
    const actuatorIconStyle = status === 'idle' ? {
        color: actuatorColor,
    } : {};
    
    return (
        <div className={`sensor-status-animation ${isActive ? 'active' : ''}`}>
            {/* Sensor Device */}
            <div className={`device-node sensor-node ${isProcessing ? 'pulsing' : ''}`}>
                <i className="ri-radar-line device-icon"></i>
                <span className="device-label">{sensorName}</span>
            </div>
            
            {/* Connection Line with Progress */}
            <div className="connection-line-wrapper">
                {/* Status text - only show for success/error states, positioned above the line */}
                {currentStage && !isProcessing && (
                    <div className={`status-text-above ${status}`}>
                        {currentStage}
                    </div>
                )}
                
                <div className="connection-line-container">
                    <svg 
                        className="connection-svg" 
                        viewBox="0 0 200 100" 
                        preserveAspectRatio="none"
                    >
                        {/* Background line */}
                        <line 
                            x1="0" 
                            y1="50" 
                            x2="200" 
                            y2="50" 
                            className="connection-line-bg"
                            strokeDasharray="5,5"
                        />
                        
                        {/* Animated progress line */}
                        {isActive && (
                            <line 
                                x1="0" 
                                y1="50" 
                                x2="200" 
                                y2="50" 
                                className={`connection-line-progress ${status}`}
                                style={{
                                    strokeDasharray: '200',
                                    strokeDashoffset: 200 - (progress * 2)
                                }}
                            />
                        )}
                        
                        {/* Animated data packets */}
                        {isProcessing && (
                            <>
                                <circle 
                                    r="4" 
                                    className="data-packet packet-1"
                                >
                                    <animateMotion 
                                        dur="2s" 
                                        repeatCount="indefinite"
                                        path="M 0,50 L 200,50"
                                    />
                                </circle>
                                <circle 
                                    r="4" 
                                    className="data-packet packet-2"
                                >
                                    <animateMotion 
                                        dur="2s" 
                                        repeatCount="indefinite"
                                        begin="0.66s"
                                        path="M 0,50 L 200,50"
                                    />
                                </circle>
                                <circle 
                                    r="4" 
                                    className="data-packet packet-3"
                                >
                                    <animateMotion 
                                        dur="2s" 
                                        repeatCount="indefinite"
                                        begin="1.33s"
                                        path="M 0,50 L 200,50"
                                    />
                                </circle>
                            </>
                        )}
                    </svg>
                </div>
            </div>
            
            {/* Actuator Device */}
            <div 
                className={`device-node actuator-node ${isProcessing ? 'pulsing' : ''} ${status}`}
                style={actuatorNodeStyle}
            >
                <i 
                    className={`${getActuatorIcon()} device-icon`}
                    style={actuatorIconStyle}
                ></i>
                <span className="device-label">{actuatorName}</span>
            </div>
        </div>
    );
}

